import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getNextAction,
  getAllQueuedActions,
  cancelQueuedActionById,
  getActionsStatus,
  rerunFailedAction,
  deleteAction,
} from "../services/compass.service";
import { INextActionResponse } from "../types/compass.types";

/**
 * Hook to get next action from queue
 */
export const useGetNextAction = () => {
  return useMutation<INextActionResponse, Error, string>({
    mutationFn: (documentId: string) => getNextAction(documentId),
    onError: (error) => {
      console.error("Failed to get next action:", error);
    },
  });
};

/**
 * Hook to get all queued actions
 */
export const useGetQueuedActions = (documentId: string) => {
  return useQuery<any, Error>({
    queryKey: ["compass-queued-actions", documentId],
    queryFn: () => getAllQueuedActions(documentId),
    enabled: !!documentId,
  });
};

/**
 * Hook to remove queued actions by IDs
 */
export const useCancelQueuedAction = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { documentId: string; jobIds: string[] }>({
    mutationFn: ({ documentId, jobIds }) => cancelQueuedActionById(documentId, jobIds),
    onSuccess: (_, variables) => {
      // Invalidate queued actions to refresh
      queryClient.invalidateQueries({
        queryKey: ["compass-queued-actions", variables.documentId],
      });
      // Also invalidate compass chat history
      queryClient.invalidateQueries({
        queryKey: ["compass-chat-history", variables.documentId],
      });
    },
    onError: (error) => {
      console.error("Failed to remove queued actions:", error);
    },
  });
};

/**
 * Hook to get actions status
 */
export const useGetActionsStatus = () => {
  return useMutation<any, Error, string[]>({
    mutationFn: (itemIds: string[]) => getActionsStatus(itemIds),
    onError: (error) => {
      console.error("Failed to get actions status:", error);
    },
  });
};

/**
 * Hook to rerun failed action
 */
export const useRerunFailedAction = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { itemId: string; documentId: string }>({
    mutationFn: ({ itemId }) => rerunFailedAction(itemId),
    onSuccess: (_, variables) => {
      // Invalidate compass chat history to reflect updated status
      queryClient.invalidateQueries({
        queryKey: ["compass-chat-history", variables.documentId],
      });
    },
    onError: (error) => {
      console.error("Failed to rerun action:", error);
    },
  });
};

/**
 * Hook to delete an action
 */
export const useDeleteAction = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { actionId: string; contextId: string }>({
    mutationFn: ({ actionId, contextId }) => deleteAction(actionId, contextId),
    onSuccess: (_, variables) => {
      // Invalidate compass chat history to reflect updated status
      queryClient.invalidateQueries({
        queryKey: ["compass-chat-history", variables.contextId],
      });
    },
    onError: (error) => {
      console.error("Failed to delete action:", error);
    },
  });
};
